import { Controller } from '@hotwired/stimulus'
import { csrfHeader } from '~/utils/csrf'

const QUANTILE_STEPS = ['p90', 'p95', 'p98', 'p99', 'max']

export default class EksContainerRecommendationController extends Controller {
  static values = {
    recommendations: Object,
    status: String,
    toggleStatusUrl: String,
    unit: String,
  }

  static targets = [
    'slider',
    'sliderLabel',
    'requestDisplay',
    'limitDisplay',
    'approveBtn',
    'statusBadge',
    'quantileInput',
    'collapseBody',
    'collapseIcon',
  ]

  connect() {
    this.updateDisplays()
  }

  toggleCollapse() {
    const body = this.collapseBodyTarget
    const icon = this.hasCollapseIconTarget ? this.collapseIconTarget : null
    const isHidden = body.style.display === 'none'
    body.style.display = isHidden ? '' : 'none'
    if (icon) {
      icon.style.transform = isHidden ? 'rotate(0deg)' : 'rotate(-90deg)'
    }
  }

  changeQuantile() {
    this.updateDisplays()
    if (this.hasQuantileInputTarget) {
      this.quantileInputTarget.value = this.currentQuantile()
    }
  }

  currentQuantile() {
    if (!this.hasSliderTarget) return 'p95'
    return QUANTILE_STEPS[parseInt(this.sliderTarget.value, 10)] || 'p95'
  }

  currentRecommendation() {
    return this.recommendationsValue[this.currentQuantile()] || {}
  }

  updateDisplays() {
    const rec = this.currentRecommendation()
    const quantile = this.currentQuantile()

    if (this.hasSliderLabelTarget) {
      this.sliderLabelTarget.textContent = quantile === 'max' ? 'Max' : quantile.toUpperCase()
    }

    if (this.hasRequestDisplayTarget && rec.request_display != null) {
      this.requestDisplayTarget.textContent = rec.request_display
    }

    if (this.hasLimitDisplayTarget && rec.limit_display != null) {
      this.limitDisplayTarget.textContent = rec.limit_display
    }

    if (this.hasQuantileInputTarget) {
      this.quantileInputTarget.value = quantile
    }
  }

  async toggleStatus(event) {
    event.preventDefault()
    const btn = this.hasApproveBtnTarget ? this.approveBtnTarget : event.currentTarget
    btn.disabled = true

    try {
      const response = await fetch(this.toggleStatusUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeader(),
        },
      })

      if (!response.ok) throw new Error('Request failed')

      const data = await response.json()
      this.statusValue = data.status
      this.updateStatusDisplay()
    } catch (error) {
      console.error('Failed to toggle recommendation status:', error)
    } finally {
      btn.disabled = false
    }
  }

  updateStatusDisplay() {
    const status = this.statusValue
    const isPending = status === 'pending'

    if (this.hasApproveBtnTarget) {
      this.approveBtnTarget.textContent = isPending ? this.approveLabel() : this.unstageLabel()
      this.approveBtnTarget.className = isPending
        ? this.approveBtnTarget.className.replace('btn-warning', 'btn-success')
        : this.approveBtnTarget.className.replace('btn-success', 'btn-warning')
    }

    if (this.hasStatusBadgeTarget) {
      // Update badge content with icons
      if (isPending) {
        this.statusBadgeTarget.className = 'badge bg-warning text-dark d-flex align-items-center gap-1'
        this.statusBadgeTarget.innerHTML = `
          <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
          </svg>
          Pending
        `
      } else {
        this.statusBadgeTarget.className = 'badge bg-success text-dark d-flex align-items-center gap-1'
        this.statusBadgeTarget.innerHTML = `
          <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
          </svg>
          Approved
        `
      }
    }
  }

  approveLabel() {
    return this.element.dataset.labelApprove || 'Approve'
  }

  unstageLabel() {
    return this.element.dataset.labelUnstage || 'Unstage'
  }

  pendingLabel() {
    return this.element.dataset.labelPending || 'Status: Pending'
  }

  approvedLabel() {
    return this.element.dataset.labelApproved || 'Status: Approved'
  }
}
